import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { Shield, Calendar, Building2 } from 'lucide-react'

import Paper from '@/components/Paper'
import { Button as VButton } from 'vnetwork-ui'
import { Skeleton } from '@/components/ui/skeleton'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { TooltipProvider } from '@/components/ui/tooltip'
import { paths } from '@/utils/path'
import {
    PERIOD_OPTIONS,
    useDashboardPeriod,
} from './hooks/use-dashboard-period'
import { useDashboardData } from './hooks/use-dashboard-data'
import KpiGrid from './sections/kpi-grid'
import OverviewCharts from './sections/overview-charts'
import ComplianceSummary from './sections/compliance-summary'
import ServicesTable from './sections/services-table'
import TicketsSection from './sections/tickets-section'
import CustomersSection from './sections/customers-section'
import EmptyState from './components/empty-state'

const SLADashboardPage = () => {
    const { t } = useTranslation('services')
    const { period, setPeriod, start, end, periodLabel } = useDashboardPeriod()

    const {
        overview,
        isLoadingOverview,
        sortedServices,
        isLoadingServices,
        ticketSummary,
        complianceOverview,
        isLoadingCompliance,
        sortedCustomers,
        isLoadingCustomers,
        compliancePercentRaw,
    } = useDashboardData(start, end)

    const hasData = overview && overview.total_services_with_sla > 0

    return (
        <TooltipProvider>
            <div className="space-y-2">
                {/* Header — single period selector for all sections */}
                <Paper className="p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-md">
                                <Shield className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold">
                                    SLA Dashboard
                                </h1>
                                <p className="text-sm text-gray-500">
                                    Theo dõi SLA toàn bộ dịch vụ
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Calendar className="w-4 h-4" />
                                <span>
                                    {dayjs(start).format('DD/MM/YYYY')} –{' '}
                                    {dayjs(end).format('DD/MM/YYYY')}
                                </span>
                            </div>
                            <Select value={period} onValueChange={setPeriod}>
                                <SelectTrigger className="w-40">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PERIOD_OPTIONS.map((opt) => (
                                        <SelectItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </Paper>

                {/* Loading state: per-section skeletons, not page-blocking */}
                {isLoadingOverview ? (
                    <div className="space-y-2">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {[1, 2, 3, 4].map((i) => (
                                <Skeleton key={i} className="h-24 w-full" />
                            ))}
                        </div>
                        <Skeleton className="h-64 w-full" />
                    </div>
                ) : !hasData ? (
                    <EmptyState
                        icon={Shield}
                        title="Chưa có dịch vụ nào được cấu hình SLA"
                        description={t(
                            'sla.dashboard.empty_period',
                            'Chưa có dữ liệu cho kỳ này. Hãy chọn kỳ khác hoặc kiểm tra lại sau.'
                        )}
                        action={
                            <Link to={paths.services}>
                                <VButton variant="contained">
                                    <Building2 className="w-4 h-4 mr-2" />
                                    Xem danh sách dịch vụ
                                </VButton>
                            </Link>
                        }
                    />
                ) : (
                    <>
                        <KpiGrid
                            overview={overview}
                            compliancePercentRaw={compliancePercentRaw}
                            ticketSummary={ticketSummary}
                        />
                        <OverviewCharts
                            overview={overview}
                            periodLabel={periodLabel}
                        />

                        {complianceOverview && (
                            <ComplianceSummary
                                overview={complianceOverview}
                                isLoading={isLoadingCompliance}
                                periodLabel={periodLabel}
                            />
                        )}

                        <ServicesTable
                            services={sortedServices}
                            isLoading={isLoadingServices}
                            periodLabel={periodLabel}
                        />

                        <TicketsSection periodLabel={periodLabel} />

                        <CustomersSection
                            customers={sortedCustomers}
                            isLoading={isLoadingCustomers}
                            periodLabel={periodLabel}
                        />
                    </>
                )}
            </div>
        </TooltipProvider>
    )
}

export default SLADashboardPage
